import React, { useState } from 'react';
import { Icon, Button } from '@repo/ui';
import { showToast } from '@repo/ui';
import { useSendEmailVerification, useCheckEmailVerificationStatus } from '@xtnd/web/features/settings/queries';

interface EmailVerificationButtonProps {
  email: string;
  verificationStatus?: 'not_started' | 'pending_verification' | 'verified' | 'failed';
  onStatusChange?: (
    email: string,
    status: 'not_started' | 'pending_verification' | 'verified' | 'failed'
  ) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

// Map SES verification statuses to UI statuses
const mapSESStatusToUI = (
  sesStatus: string
): 'not_started' | 'pending_verification' | 'verified' | 'failed' => {
  switch (sesStatus) {
    case 'Success':
      return 'verified';
    case 'Pending':
      return 'pending_verification';
    case 'Failed':
    case 'TemporaryFailure':
      return 'failed';
    case 'NotStarted':
    default:
      return 'not_started';
  }
};

export const EmailVerificationButton: React.FC<EmailVerificationButtonProps> = ({
  email,
  verificationStatus = 'not_started',
  onStatusChange,
  size = 'sm',
  disabled = false,
  className = '',
}) => {
  const [isVerifying, setIsVerifying] = useState(false);

  // Send verification email mutation
  const sendVerificationMutation = useSendEmailVerification();

  // Check verification status mutation
  const checkStatusMutation = useCheckEmailVerificationStatus();

  // Handle send verification success
  const handleSendSuccess = (result: any, email: string) => {
    console.log('Email verification result:', result);

    if (!result) {
      console.error('Email verification result is undefined');
      onStatusChange?.(email, 'failed');
      showToast.error('Email verification service returned no data. Please try again.');
      setIsVerifying(false);
      return;
    }

    if (result.success === false) {
      console.error('Email verification failed:', result.message);
      onStatusChange?.(email, 'failed');
      showToast.error(`❌ ${result.message || 'Email verification failed. Please try again.'}`);
      setIsVerifying(false);
      return;
    }

    console.log('Email verification sent successfully to:', email);
    onStatusChange?.(email, 'pending_verification');

    if (result.verificationLink) {
      showToast.success(
        `📧 Verification email sent! Check your inbox at ${email}. Click the verification link to complete setup.`
      );
    } else {
      showToast.success(
        `📧 Verification email sent! Check your inbox at ${email} and click the verification link.`
      );
    }

    setIsVerifying(false);
  };

  // Handle check status success
  const handleCheckStatusSuccess = (result: any, email: string) => {
    console.log('Email verification status result:', result);

    if (!result) {
      console.error('Email verification status result is undefined');
      onStatusChange?.(email, 'failed');
      showToast.error('Email verification status service returned no data. Please try again.');
      setIsVerifying(false);
      return;
    }

    if (!result.status) {
      console.error('Email verification status result is missing status property:', result);
      onStatusChange?.(email, 'failed');
      showToast.error(
        'Email verification status service returned invalid data. Please try again.'
      );
      setIsVerifying(false);
      return;
    }

    const uiStatus = mapSESStatusToUI(result.status);
    console.log(
      'Mapped UI status from status check:',
      uiStatus,
      'from SES status:',
      result.status
    );
    console.log(
      'Check status - Calling onStatusChange with email:',
      email,
      'and status:',
      uiStatus
    );
    onStatusChange?.(email, uiStatus);

    if (uiStatus === 'verified') {
      showToast.success('✅ Email verification confirmed!');
    } else if (uiStatus === 'failed') {
      showToast.error('❌ Email verification failed. Please try again.');
    } else if (uiStatus === 'pending_verification') {
      showToast.info(
        '📧 Email verification still pending. Check your inbox for the verification email.'
      );
    } else {
      showToast.info('Email verification status: ' + result.status);
    }
    setIsVerifying(false);
  };

  const handleVerifyEmail = () => {
    if (!email || !email.trim()) {
      showToast.error('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast.error('Please enter a valid email address');
      return;
    }

    setIsVerifying(true);
    sendVerificationMutation.mutate(email, {
      onSuccess: (result) => handleSendSuccess(result, email),
      onError: () => {
        onStatusChange?.(email, 'failed');
        setIsVerifying(false);
      },
    });
  };

  const handleCheckStatus = () => {
    if (!email || !email.trim()) {
      showToast.error('Please enter an email address');
      return;
    }

    setIsVerifying(true);
    checkStatusMutation.mutate(email, {
      onSuccess: (result) => handleCheckStatusSuccess(result, email),
      onError: () => {
        onStatusChange?.(email, 'failed');
        setIsVerifying(false);
      },
    });
  };

  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'verified':
        return <Icon name="HiCheckCircle" className="h-4 w-4 text-green-600" />;
      case 'pending_verification':
        return <Icon name="HiClock" className="h-4 w-4 text-yellow-600" />;
      case 'failed':
        return <Icon name="HiExclamationCircle" className="h-4 w-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getButton = () => {
    if (!email || !email.trim()) {
      return null;
    }

    if (verificationStatus === 'verified') {
      return (
        <Button
          onClick={handleCheckStatus}
          variant="outline"
          size={size}
          disabled={isVerifying || disabled}
          title="Refresh verification status"
          className={className}
        >
          <Icon name="HiRefresh" className="h-4 w-4" />
        </Button>
      );
    }

    if (verificationStatus === 'pending_verification') {
      return (
        <Button
          onClick={handleCheckStatus}
          variant="primary"
          size={size}
          disabled={isVerifying || disabled}
          title="Check if verification is complete"
          className={className}
        >
          {isVerifying ? (
            <Icon name="HiClock" className="h-4 w-4 animate-spin" />
          ) : (
            <Icon name="HiRefresh" className="h-4 w-4" />
          )}
        </Button>
      );
    }

    return (
      <Button
        onClick={handleVerifyEmail}
        variant="outline"
        size={size}
        disabled={isVerifying || disabled}
        title="Send verification email"
        className={className}
      >
        {isVerifying ? (
          <Icon name="HiClock" className="h-4 w-4 animate-spin" />
        ) : (
          <Icon name="HiMail" className="h-4 w-4" />
        )}
      </Button>
    );
  };

  return (
    <div className="flex items-center space-x-2">
      {getStatusIcon()}
      {getButton()}
    </div>
  );
};

export default EmailVerificationButton;
